import { useEffect, useState } from 'react';
import { useTracking } from 'react-tracking';

import { Alert, Button, Grid, Text } from '@simplywallst/ui-core';

import { useSnackbar } from '@/features/snackbar';
import { useNarrativeInvalidate } from '@/pages/Narrative/hooks/useNarrativeInvalidate';
import { Header } from '@components/ActionSheet';
import { useNarrativeSelectValuationOption } from '../../hooks/useNarrativeSelectValuationOption';
import {
  NarrativeSupportedValuationType,
  isSupportedValuationType,
} from '../../utils';
import { ValuationOptionAnalysts } from '../ValuationOptionAnalysts';
import { ValuationOptionDcf } from '../ValuationOptionDcf';
import { ValuationOptionUserDefined } from '../ValuationOptionUserDefined';
import { ValuationRow } from '../ValuationRow';
import { useValuationSelectionFormData } from './hooks/useValuationSelectionFormData';
import { getPath } from '@/router/utils';
import { ROUTE_PUBLIC_NARRATIVE_CREATE } from '@/constants/routes';
import { push } from 'redux-first-router';
import { getLocationPathname } from '@/redux/location/selectors';
import { useSelector } from 'react-redux';
import { NARRATIVE_USER_POSITION } from '@/pages/Narrative/utils/schema';

export interface Props {
  companyId: string;
  onClose: () => void;
  openOnboardingFlow: () => void;
  userHoldsPosition?: boolean;
}

export const ValuationSelectionForm = ({
  companyId,
  onClose,
  openOnboardingFlow,
  userHoldsPosition,
}: Props) => {
  const { Track, trackEvent } = useTracking();
  const [valuationType, setValuationType] =
    useState<NarrativeSupportedValuationType | null>(null);

  const { invalidate } = useNarrativeInvalidate();

  const {
    isNarrativeOnboardingComplete,
    analystsValuationOption,
    companyName,
    companyCurrencyISO,
    dcfValuationOption,
    priceTargetAnalystCount,
    tickerSymbol,
    savedValuationType,
    userDefinedValuationOption,
    userDefinedNarrativeId,
  } = useValuationSelectionFormData({
    companyId,
  });

  const pathname = useSelector(getLocationPathname);

  const {
    mutateAsync: selectValuation,
    isPending: isSaving,
    error,
  } = useNarrativeSelectValuationOption();

  const { addSnackbar } = useSnackbar();

  useEffect(() => {
    if (savedValuationType && isSupportedValuationType(savedValuationType)) {
      setValuationType(savedValuationType);
    } else {
      setValuationType(dcfValuationOption?.fairValue ? 'DCF' : 'USER_DEFINED');
    }
  }, [dcfValuationOption?.fairValue, savedValuationType]);

  const handleCreateNarrative = () => {
    trackEvent({
      action: 'click',
      target: 'create-narrative',
      valuationType: 'user_defined',
    });
    push(
      getPath({
        type: ROUTE_PUBLIC_NARRATIVE_CREATE,
        payload: {
          query: {
            company: companyId,
            returnPath: pathname,
            ...(userHoldsPosition
              ? { position: NARRATIVE_USER_POSITION.HOLDS }
              : {}),
          },
        },
      })
    );
  };

  const saveValuationSelection = async () => {
    if (!valuationType) {
      return;
    }
    trackEvent({
      action: 'save',
      valuationType,
    });

    if (valuationType === 'USER_DEFINED' && !isNarrativeOnboardingComplete)
      return openOnboardingFlow();
    if (valuationType === 'USER_DEFINED' && !userDefinedNarrativeId) {
      handleCreateNarrative();
      return;
    }
    const narrativeId =
      valuationType === 'USER_DEFINED' ? userDefinedNarrativeId : undefined;

    await selectValuation({
      input: {
        companyId,
        narrativeId,
        type: valuationType,
      },
    });

    addSnackbar({
      message: savedValuationType ? 'Fair value updated' : 'Fair value set',
      type: 'positive',
      lifeTime: 3000,
    });

    onClose();

    invalidate({ companyId, narrativeId });
  };

  const onChange = (type: NarrativeSupportedValuationType) => {
    setValuationType(type);
    trackEvent({
      action: 'set',
      valuationType: type.toLowerCase(),
    });
  };

  return (
    <Track>
      <Header>
        <Text typography="baseStrong" color="textSolid">
          Valuation for {tickerSymbol}
        </Text>
      </Header>

      <Grid
        paddingXMd="x4"
        paddingX="x2"
        paddingTop="x3"
        rowGap="8px"
        autoRows="max-content"
      >
        <ValuationRow
          selected={valuationType === 'USER_DEFINED'}
          onClick={() => onChange('USER_DEFINED')}
          data-cy-id="narrative-fair-value-modal-user-defined"
        >
          <ValuationOptionUserDefined
            narrativeId={userDefinedNarrativeId}
            fairValue={userDefinedValuationOption?.fairValue}
            intrinsicDiscount={userDefinedValuationOption?.intrinsicDiscount}
            companyId={companyId}
            companyCurrencyISO={companyCurrencyISO}
            isNarrativeOnboardingComplete={isNarrativeOnboardingComplete}
            openOnboardingFlow={openOnboardingFlow}
            isDisabled={isSaving}
            onCreateNarrative={handleCreateNarrative}
          />
        </ValuationRow>
        <ValuationRow
          selected={valuationType === 'DCF'}
          onClick={() => onChange('DCF')}
          disabled={!dcfValuationOption?.fairValue}
          data-cy-id="narrative-fair-value-modal-dcf"
        >
          <ValuationOptionDcf
            companyCurrencyISO={companyCurrencyISO}
            companyId={companyId}
            fairValue={dcfValuationOption?.fairValue}
            intrinsicDiscount={dcfValuationOption?.intrinsicDiscount}
            uniqueSymbol={tickerSymbol ?? ''}
          />
        </ValuationRow>
        <ValuationRow
          selected={valuationType === 'ANALYSTS'}
          onClick={() => onChange('ANALYSTS')}
          disabled={!analystsValuationOption?.fairValue}
          data-cy-id="narrative-fair-value-modal-analysts"
        >
          <ValuationOptionAnalysts
            fairValue={analystsValuationOption?.fairValue}
            intrinsicDiscount={analystsValuationOption?.intrinsicDiscount}
            priceTargetAnalystCount={priceTargetAnalystCount}
            companyId={companyId}
            companyName={companyName ?? ''}
            companyCurrencyISO={companyCurrencyISO}
          />
        </ValuationRow>
      </Grid>
      {error && (
        <Grid paddingXMd="x4" paddingX="x2" paddingTop="x3">
          <Alert styleType="negative">
            Failed to update fair value. Please try again.
          </Alert>
        </Grid>
      )}

      <Grid
        templateColumns="max-content max-content"
        paddingRight="x4"
        paddingY="x2"
        columnGap="8px"
        justifyContent="end"
      >
        <Button
          styleType="loudLarge"
          onClick={onClose}
          data-cy-id="narrative-fair-value-modal-close"
        >
          Close
        </Button>
        <Button
          styleType="louderLarge"
          disabled={isSaving}
          isLoading={isSaving}
          onClick={saveValuationSelection}
          data-cy-id="narrative-fair-value-modal-save"
        >
          Save
        </Button>
      </Grid>
    </Track>
  );
};
